import { Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import type { IScopedEntity } from '@mb/auth/shared';
import {
  SCOPE_ORDER,
  normalizeScopedActions,
  type PermissionActionKey,
  type PermissionScope,
} from '@mb/auth/shared';
import type { ScopedPermissionDto } from '@mb/auth/shared';

interface PermissionMatrixRow {
  entity: string;
  action: PermissionActionKey;
  actionLabel: string;
}

/** One section of the matrix — header is the entity, rows are its actions. */
interface PermissionMatrixGroup {
  entity: string;
  entityLabel: string;
  rows: PermissionMatrixRow[];
}

@Component({
  selector: 'app-permission-matrix',
  standalone: true,

  imports: [CommonModule, TranslocoPipe],
  template: `
    <div class="overflow-x-auto">
      <table class="w-full border-collapse text-sm">
        <thead>
          <tr class="border-b border-slate-200 bg-slate-50">
            <th
              class="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left font-medium text-slate-700"
            >
              {{ 'permission.matrix.action' | transloco }}
            </th>
            @for (scope of scopes(); track scope) {
              <th class="px-3 py-2 text-center font-medium text-slate-700">
                {{ getScopeLabel(scope) }}
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (group of groups(); track group.entity) {
            <tr class="bg-slate-100">
              <td
                [attr.colspan]="scopes().length + 1"
                class="sticky left-0 z-10 bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600"
              >
                {{ group.entityLabel }}
              </td>
            </tr>
            @for (row of group.rows; track row.entity + row.action) {
              <tr class="border-b border-slate-200 hover:bg-slate-50">
                <td
                  class="sticky left-0 z-10 border-r border-slate-200 bg-white px-3 py-2 font-medium text-slate-700"
                >
                  {{ row.actionLabel }}
                </td>
                @for (scope of scopes(); track scope) {
                  <td class="px-3 py-2 text-center">
                    @if (hasPermission(row.entity, row.action, scope)) {
                      <div
                        class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white"
                      >
                        <span class="material-icons text-xs">check</span>
                      </div>
                    } @else {
                      <div
                        class="inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-slate-300"
                      ></div>
                    }
                  </td>
                }
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class PermissionMatrixComponent {
  readonly #transloco = inject(TranslocoService);

  readonly entities = input.required<IScopedEntity[]>();
  readonly permissions = input.required<ScopedPermissionDto[]>();

  readonly scopes = computed(() => [...SCOPE_ORDER]);

  readonly groups = computed<PermissionMatrixGroup[]>(() =>
    this.entities().map((entity) => ({
      entity: entity.entity,
      entityLabel: this.#transloco.translate(entity.label),
      rows: normalizeScopedActions(entity).map(({ action, label }) => ({
        entity: entity.entity,
        action,
        actionLabel: this.#transloco.translate(label),
      })),
    })),
  );

  hasPermission(
    entity: string,
    action: string,
    scope: PermissionScope,
  ): boolean {
    return this.permissions().some(
      (p) => p.entity === entity && p.action === action && p.scope === scope,
    );
  }

  getScopeLabel(scope: PermissionScope): string {
    return this.#transloco.translate(`permission.scope.${scope}`);
  }
}
